import React from 'react';
import { FaCircleCheck } from 'react-icons/fa6';
import { CgClose } from 'react-icons/cg';
import styled from 'styled-components';

import { Flex } from '../../components/ui/Flex';
import { LoadingSpinner } from '../../components/ui/Select';
import { Palette } from '../../components/utils';
import { formatMbFileSize } from '../../utils/utils';
import {
  DOCUMENT_FILE_TYPE_CATEGORY_STYLES,
  getDocumentFileTypeCategory,
  getDocumentFileTypeChipLabel
} from './constants';
import {
  DocumentClearFileButton,
  DocumentFilePreview,
  DocumentFilePreviewInfo,
  DocumentFilePreviewName,
  DocumentFileSize,
  DocumentFileTypeIcon,
  DocumentUploadProgressFill,
  DocumentUploadProgressRow,
  DocumentUploadProgressTrack,
  DocumentUploadStatusText
} from './documentWizardStyles';

const CheckIcon = styled(FaCircleCheck)`
  width: 1.5rem;
  height: 1.5rem;
  fill: ${Palette.ACTIVE_GREEN};
`;

const UploadSpinner = styled(LoadingSpinner)`
  width: 1.25rem;
  height: 1.25rem;
  color: ${Palette.HAZY_BLUE};
`;

const RowPreviewImage = styled.img`
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid ${Palette.STROKE_GREY};
`;

function DocumentUploadedFileRow({
  t,
  file,
  fileName,
  fileSizeBytes,
  previewImageUrl,
  onClearFile,
  clearDisabled = false,
  isUploading = false,
  uploadProgress = null,
  uploadStalled = false
}) {
  const displayFileName = file?.name || fileName;
  if (!displayFileName) {
    return null;
  }

  const fileCategory = getDocumentFileTypeCategory(displayFileName);
  const fileTypeLabel = getDocumentFileTypeChipLabel(displayFileName);
  const fileTypeStyles = fileCategory
    ? DOCUMENT_FILE_TYPE_CATEGORY_STYLES[fileCategory]
    : null;
  const progressPercent =
    typeof uploadProgress === 'number'
      ? Math.round(Math.min(1, Math.max(0, uploadProgress)) * 100)
      : null;
  const sizeBytes = file?.size ?? fileSizeBytes;
  const showActions = Boolean(onClearFile);
  const fileSizeLabel = (() => {
    if (sizeBytes == null || sizeBytes === '') {
      return null;
    }
    if (typeof sizeBytes === 'string' && /[A-Za-z]/.test(sizeBytes)) {
      return sizeBytes;
    }
    if (Number.isNaN(Number(sizeBytes))) {
      return null;
    }
    return `${formatMbFileSize(Number(sizeBytes))}MB`;
  })();

  return (
    <DocumentUploadProgressRow>
      <DocumentFilePreview justify="space-between" alignItems="center" gapY="1rem">
        <Flex gapY="0.625rem" alignItems="center" style={{ minWidth: 0, flex: 1 }}>
          {previewImageUrl ? (
            <RowPreviewImage src={previewImageUrl} alt="" aria-hidden />
          ) : (
            <DocumentFileTypeIcon
              $background={fileTypeStyles?.background}
              $color={fileTypeStyles?.color}
              aria-hidden>
              {fileTypeLabel}
            </DocumentFileTypeIcon>
          )}
          <DocumentFilePreviewInfo>
            <DocumentFilePreviewName title={displayFileName}>
              {displayFileName}
            </DocumentFilePreviewName>
            {fileSizeLabel ? <DocumentFileSize>{fileSizeLabel}</DocumentFileSize> : null}
          </DocumentFilePreviewInfo>
        </Flex>
        {showActions ? (
          <Flex gapY="1.1875rem" alignItems="center" style={{ flexShrink: 0 }}>
            {isUploading ? (
              <>
                <DocumentUploadStatusText $stalled={uploadStalled}>
                  {uploadStalled
                    ? t('Connection stalled, retrying…', {
                        defaultValue: 'Connection stalled, retrying…'
                      })
                    : progressPercent != null
                      ? t('Uploading {{percent}}%', {
                          defaultValue: `Uploading ${progressPercent}%`,
                          percent: progressPercent
                        })
                      : t('Uploading…', { defaultValue: 'Uploading…' })}
                </DocumentUploadStatusText>
                <UploadSpinner aria-hidden />
              </>
            ) : (
              <>
                <p>100%</p>
                <CheckIcon />
                {onClearFile && !clearDisabled ? (
                  <DocumentClearFileButton
                    type="button"
                    onClick={onClearFile}
                    aria-label={t('Remove')}>
                    <CgClose />
                  </DocumentClearFileButton>
                ) : null}
              </>
            )}
          </Flex>
        ) : null}
      </DocumentFilePreview>
      {isUploading && progressPercent != null ? (
        <DocumentUploadProgressTrack
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPercent}
          aria-label={t('Upload progress', { defaultValue: 'Upload progress' })}>
          <DocumentUploadProgressFill
            $progress={uploadProgress}
            $stalled={uploadStalled}
          />
        </DocumentUploadProgressTrack>
      ) : null}
    </DocumentUploadProgressRow>
  );
}

export default DocumentUploadedFileRow;
